import { BaseExportService } from "./BaseExportService";

export class EnterpriseAmlExportService extends BaseExportService {
  constructor(data, metadata = {}) {
    super(data, metadata);

    this.response_info = this.extract_response_info();
    this.request_data = this.extract_request_data();
    this.wls_data = this.extract_wls_data();
    this.watchlist_summary = this.extract_watchlist_summary();
    this.verification_status = this.extract_verification_status();
  }

  // ─────────────────────────────────────────────────────────────────
  // Data extraction
  // ─────────────────────────────────────────────────────────────────

  get_root() {
    return this.data?.result || this.data || {};
  }

  get_first_applicant() {
    const root = this.get_root();
    return root?.Fields?.Applicants_IO?.Applicant?.[0] || {};
  }

  extract_response_info() {
    const root = this.get_root();
    return root?.ResponseInfo || this.metadata?.response_info || {};
  }

  extract_request_data() {
    const root = this.get_root();
    return root?.RequestData || this.metadata?.request_data || {};
  }

  /** DSWatchlistVerification — contains WlsResults (may be null when no matches). */
  extract_wls_data() {
    const applicant = this.get_first_applicant();
    return applicant?.Attributes?.DSWatchlistVerification || {};
  }

  /** DRWatchlistVerification — summary counts/decision. */
  extract_watchlist_summary() {
    const applicant = this.get_first_applicant();
    return applicant?.Attributes?.DRWatchlistVerification || {};
  }

  extract_verification_status() {
    const applicant = this.get_first_applicant();
    return applicant?.DSWatchlistVerificationStatus || {};
  }

  // ─────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────

  safe_val(val) {
    if (val === null || val === undefined || val === 'None') return 'N/A';
    return val;
  }

  format_string_array(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return 'N/A';
    return arr.join('; ');
  }

  get_filename_prefix() {
    const name = this.request_data?.EnterpriseName || 'Unknown';
    const app_id = this.response_info?.ApplicationId || 'Unknown';
    return `Enterprise_AML_Report_${name}_${app_id}`;
  }

  get_available_sections() {
    return {
      subject_information: 'Subject Information',
      screening_summary: 'Screening Summary',
      watchlist_results: 'Watchlist Results',
      negative_media: 'Negative Media Results',
      raw_json: 'Raw JSON Data',
    };
  }

  get_default_export_options() {
    return {
      subject_information: false,
      screening_summary: false,
      watchlist_results: false,
      negative_media: false,
      raw_json: false,
    };
  }

  prepare_export_data(export_options) {
    const export_data = {};

    if (export_options.subject_information) {
      export_data.subject_information = this.prepare_subject_data();
    }

    if (export_options.screening_summary) {
      export_data.screening_summary = this.prepare_screening_summary();
    }

    if (export_options.negative_media) {
      export_data.negative_media = this.prepare_negative_media_data();
    }

    if (export_options.watchlist_results) {
      export_data.watchlist_results = this.prepare_watchlist_results_data();
    }

    if (export_options.raw_json) {
      export_data.raw_json = [this.prepare_raw_json_data()];
    }

    return export_data;
  }

  /**
   * Subject info uses EnterpriseName and WlsResults count.
   */
  prepare_subject_data() {
    const wls_results = this.wls_data?.WlsResults || [];
    const nm_results = this.wls_data?.NmResults || [];
    const total = (wls_results.length || 0) + (nm_results.length || 0);

    return [{
      'Application ID': this.safe_val(this.response_info?.ApplicationId),
      'Solution Set Instance ID': this.safe_val(this.response_info?.SolutionSetInstanceId),
      'Current Queue': this.safe_val(this.response_info?.CurrentQueue),
      'Enterprise Name': this.safe_val(this.request_data?.EnterpriseName),
      'Verification Status': this.safe_val(this.verification_status?.Outcome),
      'Verification Success': this.verification_status?.IsSuccess ?? 'N/A',
      'Total Watchlist Results': total,
      'Total WLS Results': wls_results.length,
      'Total Negative Media Results': nm_results.length,
    }];
  }

  /**
   * Prepare WlsResults for export.
   * Handles the case where DSWatchlistVerification is null (no matches).
   */
  prepare_watchlist_results_data() {
    const wls_results = this.wls_data?.WlsResults || [];

    if (wls_results.length === 0) {
      return [{ 'Info': 'No watchlist results found.' }];
    }

    return wls_results.map((result, index) => ({
      'Result #': index + 1,
      'Product ID': this.safe_val(result.productId),
      'Score': result.score ?? 'N/A',
      'Source ID': this.safe_val(result.sourceId),
      'Category': this.safe_val(result.category),
      'Result ID': this.safe_val(result.resultId),
      'Subject Matched': this.safe_val(result.subjectMatched),
      'Entity Name': this.safe_val(result.entityName),
      'Individual Name': this.safe_val(result.individualName),
      'Date of Birth': this.safe_val(result.dateOfBirth),
      'Dates of Birth (Normalised)': this.safe_val(result.datesOfBirthNormalized),
      'Birth Years': this.safe_val(result.birthYears),
      'Age': this.safe_val(result.age),
      'Gender': this.safe_val(result.gender),
      'Nationality': this.safe_val(result.nationality),
      'Place of Birth': this.safe_val(result.placeOfBirth),
      'Address': this.safe_val(result.address),
      'Eye Color': this.safe_val(result.eyeColor),
      'Hair Color': this.safe_val(result.hairColor),
      'Height': this.safe_val(result.height),
      'Race': this.safe_val(result.race),
      'Identifier': this.safe_val(result.identifier),
      'Program': this.safe_val(result.program),
      'Caution': this.safe_val(result.caution),
      'Alias List': this.safe_val(result.aliasList),
      'Remarks': this.safe_val(result.remarks),
      'Source Agency Name': this.safe_val(result.sourceAgencyName),
      'Source Agency Acronym': this.safe_val(result.sourceAgencyAcronym),
      'Source Parent Agency': this.safe_val(result.sourceParentAgency),
      'Source List Type': this.safe_val(result.sourceListType),
      'Source Region': this.safe_val(result.sourceRegion),
      'Source Country': this.safe_val(result.sourceCountry),
      'WLS Result Type': this.safe_val(result.wlsResultType),
      'Weight': this.safe_val(result.weight),
      'Highlights': this.format_string_array(result.highlights),
      'Text': this.safe_val(this.strip_html(result.text)),
      'URL': this.safe_val(result.url),
    }));
  }

  /**
   * Prepare Negative Media (NmResults) for export.
   * Uses DSWatchlistVerification.NmResults when present.
   */
  prepare_negative_media_data() {
    const nm_results = this.wls_data?.NmResults || [];

    if (nm_results.length === 0) {
      return [{ Info: 'No negative media results found.' }];
    }

    return nm_results.map((result, index) => ({
      'Result #': index + 1,
      'Product ID': this.safe_val(result.productId),
      'Score': result.score ?? 'N/A',
      'Source ID': this.safe_val(result.sourceId),
      'Category': this.safe_val(result.category),
      'Result ID': this.safe_val(result.resultId),
      'Source Country': this.safe_val(result.sourceCountry),
      'Subject Matched': this.safe_val(result.subjectMatched),
      'Subject Age': this.safe_val(result.subjectAge),
      'Subject Location': this.safe_val(result.subjectLocation),
      'Article Title': this.safe_val(result.articleTitle),
      'Article Date': this.safe_val(result.articleDate),
      'Text': this.safe_val(this.strip_html(result.text)),
      'URL': this.safe_val(result.url),
      'Highlights': this.format_string_array(result.highlights),
      'Concepts': this.format_string_array(result.concepts),
    }));
  }

  /**
   * Prepare screening summary (DRWatchlistVerification).
   */
  prepare_screening_summary() {
    return [{
      'Watchlist Decision': this.safe_val(this.watchlist_summary?.WatchlistDecision),
      'Match Count': this.watchlist_summary?.MatchCount ?? 'N/A',
      'API Response Time (ms)': this.safe_val(this.watchlist_summary?.APIResponseTime),
      'Time of Request': this.safe_val(this.watchlist_summary?.TimeOfRequest),
      'Service Time (ms)': this.watchlist_summary?.ServiceTime ?? 'N/A',
      'Response Size (bytes)': this.watchlist_summary?.ResponseSize ?? 'N/A',
      'Verification Outcome': this.safe_val(this.verification_status?.Outcome),
      'Verification Success': this.verification_status?.IsSuccess ?? 'N/A',
    }];
  }

  prepare_raw_json_data() {
    return {
      ResponseInfo: this.response_info,
      RequestData: this.request_data,
      DSWatchlistVerification: this.wls_data,
      DRWatchlistVerification: this.watchlist_summary,
      VerificationStatus: this.verification_status,
    };
  }
}

